/**
 * RIFT — leaderboard.
 *
 * Global ranked list of all accounts, sorted by level (descending), then XP,
 * then join date. Opened from the main menu's LEADERBOARD nav item.
 * Self-contained with its own scoped `.rl-` stylesheet.
 *
 *     const leaderboard = new Leaderboard(host, { playerName, onClose });
 *     leaderboard.show();
 *
 * The leaderboard is a read-only ranked list; no interaction beyond scrolling
 * and closing. The current player's row is highlighted if present.
 */

import { HUD_TOKENS, HUD_CSS, mountCorners } from './theme.js';

const STYLE_ID = 'rl-leaderboard-style';
const API_ROOT = '/mp/progression';

export class Leaderboard {
  constructor(host, opts = {}) {
    this.host = host || document.body;
    this.onClose = opts.onClose ?? null;
    this.playerName = opts.playerName ?? null;
    this.visible = false;
    this.entries = [];
    this.loading = true;
    this.error = null;

    this._injectStyle();
    this._build();
    this._fetchLeaderboard();
  }

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  _build() {
    this.root = this._el('section', 'rl-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Leaderboard');
    this.root.hidden = true;
    mountCorners(this.root);

    const head = this._el('div', 'rl-head', this.root);
    const headL = this._el('div', null, head);
    this._el('div', 'hud-eyebrow', headL, 'LEADERBOARD');
    this._el('h1', 'rl-title hud-title', headL, 'GLOBAL RANKINGS');
    this._el(
      'p',
      'rl-copy',
      headL,
      'All accounts ranked by level and experience. Climb the ranks and earn your place.'
    );
    const close = this._el('button', 'hud-close rl-headclose', head, 'CLOSE');
    close.type = 'button';
    close.addEventListener('click', () => this._close());

    this.table = this._el('div', 'rl-table', this.root);
    this._renderTable();

    const foot = this._el('div', 'rl-foot', this.root);
    this.status = this._el('div', 'rl-status', foot, '');
    const closeBtn = this._el('button', 'hud-btn rl-close', foot, 'CLOSE');
    closeBtn.type = 'button';
    closeBtn.addEventListener('click', () => this._close());

    this.host.appendChild(this.root);
  }

  _renderTable() {
    // Clear existing rows
    this.table.innerHTML = '';

    // Header
    const header = this._el('div', 'rl-header', this.table);
    this._el('div', 'rl-col rl-rank', header, '#');
    this._el('div', 'rl-col rl-name', header, 'Name');
    this._el('div', 'rl-col rl-level', header, 'Level');
    this._el('div', 'rl-col rl-joined', header, 'Joined');

    if (this.loading) {
      const loading = this._el('div', 'rl-loading', this.table, '⟳ Loading rankings...');
      loading.setAttribute('role', 'status');
      loading.setAttribute('aria-live', 'polite');
      return;
    }

    if (this.error) {
      const error = this._el('div', 'rl-error', this.table, `⚠ ${this.error}`);
      error.setAttribute('role', 'alert');
      return;
    }

    // Rows
    for (const entry of this.entries) {
      const row = this._el('div', 'rl-row', this.table);
      if (entry.name === this.playerName) {
        row.classList.add('rl-current');
        row.setAttribute('aria-label', `Your rank: ${entry.rank}`);
      }

      this._el('div', 'rl-col rl-rank', row, String(entry.rank));
      this._el('div', 'rl-col rl-name', row, entry.name);
      this._el('div', 'rl-col rl-level', row, `Lvl ${entry.level}`);

      const joinDate = new Date(entry.created * 1000);
      const joined = joinDate.toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
      this._el('div', 'rl-col rl-joined', row, joined);
    }
  }

  async _fetchLeaderboard() {
    try {
      const response = await fetch(`${API_ROOT}/leaderboard`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const json = response.headers.get('content-type')?.includes('application/json');
      const body = json ? await response.json().catch(() => ({})) : null;

      if (!response.ok || !body?.entries) {
        throw new Error(body?.error || 'Could not fetch leaderboard');
      }

      this.entries = body.entries;
      this.error = null;
    } catch (err) {
      this.error = err.message || 'Leaderboard service is unavailable';
      this.entries = [];
      console.error('[leaderboard] fetch failed', err);
    } finally {
      this.loading = false;
      this._renderTable();
      this._updateStatus();
    }
  }

  _updateStatus() {
    if (this.error) {
      this.status.textContent = `⚠ Error: ${this.error}`;
    } else if (this.entries.length > 0) {
      const current = this.entries.find((e) => e.name === this.playerName);
      if (current) {
        this.status.textContent = `Your Rank: #${current.rank} · Level ${current.level}`;
        this.status.classList.add('rl-status-highlight');
      } else if (this.playerName) {
        this.status.textContent = `Showing top ${this.entries.length} players`;
      } else {
        this.status.textContent = `Top ${this.entries.length} accounts`;
      }
    } else {
      this.status.textContent = 'No entries available';
    }
  }

  _close() {
    this.hide();
    this.onClose?.();
  }

  show() {
    if (this.visible) return;
    this.visible = true;
    this.root.hidden = false;
    void this.root.offsetWidth;
    this.root.classList.add('rl-live');
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('rl-live');
    this.root.hidden = true;
  }

  dispose() {
    this.hide();
    try {
      this.root?.remove();
    } catch {
      /* already gone */
    }
  }
}

const CSS = `
/* The display:flex below outranks the UA's [hidden]{display:none}, so without
   this closing the screen leaves a transparent z-index:60 sheet across the
   viewport that silently eats every click on the menu underneath. */
.rl-root[hidden]{display:none!important}
.rl-root {
  ${HUD_TOKENS}
  position:fixed; inset:0; z-index:60; display:flex; flex-direction:column;
  gap:clamp(10px,1.6vh,20px); padding:clamp(16px,3vh,44px) clamp(18px,4vw,64px);
  background:
    radial-gradient(60% 55% at 82% 18%, rgba(182,146,255,.16) 0%, transparent 60%),
    radial-gradient(55% 60% at 14% 88%, rgba(255,176,42,.18) 0%, transparent 65%),
    radial-gradient(140% 100% at 50% 50%, rgba(22,32,42,.92) 0%, rgba(4,6,9,.99) 70%),
    linear-gradient(180deg, #0a0e14 0%, #04060a 100%);
  color:var(--ink); font-family:var(--ff); text-transform:uppercase; letter-spacing:.05em;
  opacity:0; transition:opacity .22s ease; overflow:hidden;
}
.rl-root.rl-live { opacity:1; }
.rl-root > * { position:relative; z-index:1; }
${HUD_CSS}

.rl-head { display:flex; align-items:flex-start; justify-content:space-between; gap:28px; flex:0 0 auto;
  padding:24px 30px;
  background: linear-gradient(90deg, rgba(18,26,34,.96) 0%, rgba(10,16,21,.85) 60%, rgba(8,13,17,.55) 100%);
  border:1px solid rgba(190,214,230,.3); clip-path: var(--shear);
  position:relative; box-shadow: 0 14px 44px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08); }
.rl-head::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.rl-head::after { content:''; position:absolute; left:0; top:14px; bottom:14px; width:5px;
  background: linear-gradient(180deg, transparent, var(--amber) 25%, var(--amber-2) 75%, transparent);
  box-shadow: 0 0 20px rgba(255,176,42,.78), 0 0 40px rgba(255,176,42,.3); }
.rl-title { margin:.05em 0 .35em; font-size:clamp(34px,4vw,56px); line-height:1; }
.rl-copy { margin:6px 0 0; max-width:64ch; color:var(--ink-2); font-size:12.5px; letter-spacing:.04em;
  text-transform:none; line-height:1.6; }
.rl-headclose { margin-top:6px; }

.rl-table { flex:1 1 auto; display:flex; flex-direction:column; gap:0; overflow:auto; padding:0 2px;
  background: rgba(4,7,11,.3); }

.rl-header { display:grid; grid-template-columns:80px 1fr 100px 120px; gap:12px;
  padding:12px 16px; background:linear-gradient(90deg, rgba(18,26,34,.7) 0%, rgba(10,16,21,.5) 100%);
  border-bottom:1px solid rgba(190,214,230,.2); position:sticky; top:0; z-index:10;
  font-family:var(--fm); font-size:10px; letter-spacing:.2em; color:var(--ink-3); }

.rl-row { display:grid; grid-template-columns:80px 1fr 100px 120px; gap:12px;
  padding:12px 16px; border-bottom:1px solid rgba(190,214,230,.12);
  transition:background .16s ease, border-color .16s ease;
  align-items:center; }

.rl-row:hover { background:rgba(255,176,42,.08); border-color:rgba(255,176,42,.2); }

.rl-row.rl-current { background:
  linear-gradient(90deg, rgba(255,176,42,.15) 0%, transparent 100%);
  border-color:rgba(255,176,42,.35);
  box-shadow: inset 0 0 12px rgba(255,176,42,.12); }
.rl-row.rl-current .rl-rank { color:var(--amber); font-weight:800; }
.rl-row.rl-current .rl-name { color:#fff; font-weight:700; text-shadow: 0 0 12px rgba(255,176,42,.6); }
.rl-row.rl-current .rl-level { color:var(--amber); font-weight:700; }

.rl-col { display:flex; align-items:center; font-size:13px; color:var(--ink-2); }
.rl-rank { justify-content:center; font-family:var(--fd); font-weight:600; color:var(--amber-2);
  text-shadow: 0 0 8px rgba(255,176,42,.4); }
.rl-name { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#fff;
  font-weight:500; text-shadow: 0 1px 4px rgba(0,0,0,.4); }
.rl-level { text-align:center; font-family:var(--fm); font-weight:700; }
.rl-joined { text-align:right; font-family:var(--fm); font-size:11px; }

.rl-loading { padding:32px 16px; text-align:center; color:var(--amber);
  font-size:13px; letter-spacing:.08em; animation:rl-spin 2s linear infinite; }
@keyframes rl-spin { 0% { transform:rotate(0deg); } 100% { transform:rotate(360deg); } }

.rl-error { padding:24px 16px; text-align:center; color:var(--red);
  font-size:12px; letter-spacing:.06em; }

.rl-foot { display:flex; align-items:center; gap:16px; flex:0 0 auto;
  padding:14px 22px; background:linear-gradient(90deg, rgba(12,18,24,.92), rgba(8,13,17,.7));
  border:1px solid rgba(190,214,230,.26); clip-path: var(--shear-sm);
  position:relative; }
.rl-foot::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }

.rl-status { font-family:var(--fm); font-size:10px; letter-spacing:.16em; color:var(--ink-3);
  flex:1; text-shadow: 0 0 8px rgba(0,0,0,.4); }
.rl-status-highlight { color:var(--amber-3); text-shadow: 0 0 12px rgba(255,176,42,.6); }

.rl-close { margin-left:auto; padding:12px 30px; font-weight:800; }

@media (max-width: 768px) {
  .rl-header, .rl-row { grid-template-columns:50px 1fr 70px 100px; gap:8px; padding:10px 12px; }
  .rl-col { font-size:12px; }
  .rl-rank { font-size:11px; }
  .rl-joined { font-size:9px; }
}
`;
